// Leads List Page - Clean & Minimal

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getLeads, getCustomers, getFormIdentifiers, getFormCounts } from '@/lib/api';
import { useAuthStore } from '@/hooks/useAuth';
import FormTabs from '@/components/leads/FormTabs';
import { LeadFilters, PaginationParams } from '@/lib/types';
import { downloadLeadsCSV } from '@/lib/export';
import LeadFiltersComponent from '@/components/leads/LeadFilters';
import LeadTable from '@/components/leads/LeadTable';
import BulkActions from '@/components/leads/BulkActions';
import Button from '@/components/ui/Button';
import toast from 'react-hot-toast';

/** `fixedCustomerSlug`: eingebettet im Kunden-Arbeitsbereich — der Kunde steht fest. */
export default function LeadsPage({ fixedCustomerSlug }: { fixedCustomerSlug?: string } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const { user } = useAuthStore();
  
  const isCustomer = user?.role === 'customer';
  
  const [filters, setFilters] = useState<LeadFilters>({
    customer_slug: fixedCustomerSlug || searchParams.get('customer') || undefined,
    project_id: searchParams.get('project_id') || undefined,
    form_identifier: searchParams.get('form') || undefined,
    status: searchParams.get('status') as any || undefined,
    search: searchParams.get('search') || undefined,
  });

  const [pagination, setPagination] = useState<PaginationParams>({
    page: parseInt(searchParams.get('page') || '1'),
    per_page: parseInt(searchParams.get('per_page') || '25'),
  });

  useEffect(() => {
    const params: any = {};
    // Eingebettet: das `tab` der umgebenden Seite bleibt stehen, der feste
    // Kunde wandert nicht in die URL.
    if (fixedCustomerSlug && searchParams.get('tab')) params.tab = searchParams.get('tab');
    if (filters.customer_slug && !fixedCustomerSlug) params.customer = filters.customer_slug;
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.form_identifier) params.form = filters.form_identifier;
    if (filters.status) params.status = filters.status;
    if (filters.search) params.search = filters.search;
    if (pagination.page > 1) params.page = pagination.page.toString();
    if (pagination.per_page !== 25) params.per_page = pagination.per_page.toString();
    
    setSearchParams(params, { replace: true });
  }, [filters, pagination, setSearchParams]);

  // Leads laden - RLS filtert automatisch für Kunden
  const {
    data: leadsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['leads', filters, pagination],
    queryFn: () => getLeads(filters, pagination),
  });

  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
  });

  const { data: formIdentifiers } = useQuery({
    queryKey: ['form-identifiers'],
    queryFn: () => getFormIdentifiers(),
  });

  const { data: formCounts } = useQuery({
    queryKey: ['form-counts'],
    queryFn: () => getFormCounts(),
  });

  const handleFilterChange = (newFilters: LeadFilters) => {
    setFilters(fixedCustomerSlug ? { ...newFilters, customer_slug: fixedCustomerSlug } : newFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPagination({ page: 1, per_page: newPerPage });
  };

  const handleClearFilters = () => {
    setFilters(fixedCustomerSlug ? { customer_slug: fixedCustomerSlug } : {});
    setPagination({ page: 1, per_page: 25 });
  };

  const handleExport = () => {
    if (!leadsData?.data || leadsData.data.length === 0) {
      toast.error('Keine Leads zum Exportieren');
      return;
    }
    downloadLeadsCSV(leadsData.data);
    toast.success('CSV-Export erfolgreich');
  };

  const handleToggleSelectAll = () => {
    if (selectedLeadIds.length === leadsData?.data.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(leadsData?.data.map((l) => l.id) || []);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedLeadIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleClearSelection = () => {
    setSelectedLeadIds([]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {!fixedCustomerSlug && <h1 className="text-2xl font-semibold text-neutral-900">Anfragen</h1>}
          <p className="text-neutral-500 mt-1">
            {leadsData?.total || 0} {leadsData?.total === 1 ? 'Anfrage' : 'Anfragen'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport}>
            CSV Export
          </Button>
          <Button variant="secondary" onClick={() => refetch()}>
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Form Tabs */}
      {formIdentifiers && formIdentifiers.length > 1 && (
        <FormTabs
          formIdentifiers={formIdentifiers}
          selectedForm={filters.form_identifier}
          onSelectForm={(form) => handleFilterChange({ ...filters, form_identifier: form })}
          formCounts={formCounts}
        />
      )}

      {/* Filters */}
      <LeadFiltersComponent
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        customers={isCustomer ? [] : (customers || [])}
        formIdentifiers={formIdentifiers || []}
      />

      {/* Bulk Actions */}
      <BulkActions
        selectedIds={selectedLeadIds}
        onClearSelection={handleClearSelection}
      />

      {/* Leads Table - Grouped by Customer */}
      {(() => {
        const leads = leadsData?.data || [];
        
        // Gruppiere Leads nach Kunden
        const leadsByCustomer: Record<string, any[]> = {};
        
        leads.forEach(lead => {
          const customerId = lead.project?.customer_id || 'ohne';
          if (!leadsByCustomer[customerId]) {
            leadsByCustomer[customerId] = [];
          }
          leadsByCustomer[customerId].push(lead);
        });

        // Sortiere Kunden alphabetisch
        const sortedCustomerIds = Object.keys(leadsByCustomer).sort((a, b) => {
          if (a === 'ohne') return 1;
          if (b === 'ohne') return -1;
          const nameA = leadsByCustomer[a][0]?.project?.customer?.name || '';
          const nameB = leadsByCustomer[b][0]?.project?.customer?.name || '';
          return nameA.localeCompare(nameB);
        });

        if (isLoading) {
          return (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900" />
            </div>
          );
        }

        if (leads.length === 0) {
          return (
            <div className="bg-white border border-neutral-200 rounded-lg p-12 text-center">
              <p className="text-neutral-500">Keine Anfragen gefunden</p>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            {sortedCustomerIds.map(customerId => {
              const customerLeads = leadsByCustomer[customerId];
              const customerName = customerLeads[0]?.project?.customer?.name || 'Ohne Kunde';

              return (
                <div key={customerId} className="space-y-3">
                  {/* Customer Header */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <h2 className="text-lg font-semibold text-gray-900 px-4">
                      {customerName}
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({customerLeads.length} {customerLeads.length === 1 ? 'Anfrage' : 'Anfragen'})
                      </span>
                    </h2>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Leads for this customer */}
                  <LeadTable
                    leads={customerLeads}
                    isLoading={false}
                    selectedIds={selectedLeadIds}
                    onToggleSelect={handleToggleSelect}
                    onToggleSelectAll={handleToggleSelectAll}
                    pagination={{
                      page: pagination.page,
                      perPage: pagination.per_page,
                      total: leadsData?.total || 0,
                      totalPages: leadsData?.total_pages || 0,
                    }}
                    onPageChange={handlePageChange}
                    onPerPageChange={handlePerPageChange}
                    onRefetch={refetch}
                    hidePagination={sortedCustomerIds.length > 1} // Hide pagination when grouped
                  />
                </div>
              );
            })}

            {/* Global Pagination (shown once at bottom when grouped) */}
            {sortedCustomerIds.length > 1 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Seite {pagination.page} von {leadsData?.total_pages || 0} • {leadsData?.total || 0} gesamt
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      Zurück
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= (leadsData?.total_pages || 0)}
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
